import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip} from "recharts";

import axios from "axios";
import moment from "moment";

import {GridContainer, GridItem} from '../../../../../../../Common/DesignSystem/Grid';
import { ActivityCard, ActivityCardItem } from '../../../../../../../Common/DesignSystem/ActivityCard';
import Select from 'react-select';
import { Trans } from 'react-i18next';
import colours from '../../../../../../../Common/DesignSystem/Colours';
import { P2, P3, Heading5} from '../../../../../../../Common/DesignSystem/Text';
import { Gauge } from '../../../../../../../Common/DesignSystem/Gauge';

import { VehicleActivity } from './VehicleActivity';

const setupBookingsBreakdown = (bookings_list, num_months) => {
    var bookings_breakdown = [];

    //Sort the given list first so its easier to search
    var sorted_bookings = {};
    for (var i = 0; i < bookings_list.length; i++) {
        if (! (bookings_list[i].year in sorted_bookings)) {
            sorted_bookings[bookings_list[i].year] = {}
        }
        sorted_bookings[bookings_list[i].year][bookings_list[i].month] = bookings_list[i].bookings;
    }

    //Loop through bookings in list, starting from today, go back a month at a time and check if 
    var current_month = parseInt(moment().month()) + 1;
    var current_year = parseInt(moment().year());

    for (i = num_months; i > 0; --i) {
        if (current_year in sorted_bookings && current_month in sorted_bookings[current_year]) {
            bookings_breakdown.unshift({
                name: moment(current_month, 'M').format('MMM'),
                Bookings: sorted_bookings[current_year][current_month],
                pv: sorted_bookings[current_year][current_month],
                amt: sorted_bookings[current_year][current_month]
            })
        } else {
            bookings_breakdown.unshift({
                name: moment(current_month, 'M').format('MMM'),
                Bookings: 0,
                pv: 0,
                amt: 0
            })
        }

        current_month = current_month - 1;
        if (current_month === 0) {
            current_year = current_year - 1;
            current_month = 12;
        }
    }

    return bookings_breakdown;
}

const VehicleOverviewTab = (props) => {
	const [bookingsBreakdown, setBookingsBreakdown] = useState(setupBookingsBreakdown(props.vehicle_details.booking_breakdown, 12));
	const [loading, setLoading] = useState(false);
    const [statsTimeline, setStatsTimeline] = useState(12);
    const [totalReports, setTotalReports] = useState(-1);
    const [totalNewDamageReports, setTotalNewDamageReports] = useState(-1);
    const [expenses, setExpenses] = useState([]);
    const [totalExpenses, setTotalExpenses] = useState(-1);
    const [expenseTickets, setExpenseTickets] = useState(-1);
    const [expenseDamage, setExpenseDamage] = useState(-1);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        window.addEventListener("resize", handleResize);

        retrieveExpenses();
        retrieveReports(props.reports);

        // necessary workaround to have the bookings chart and media rating component render correctly on mobile
        // render with a desktop width of 1000px, and within 300ms, change it to actual width
        setWindowWidth(1000);
        setTimeout(()=> {
            handleResize();
        }, 300);

        return () => {
            window.removeEventListener("resize", handleResize);
        }
    }, []);

    useEffect(() => {
        retrieveReports(props.reports);
    }, [props.reports]);

    const handleResize = () => {
        setWindowWidth(window.innerWidth);
    }

    const retrieveReports = (reports = []) => {
        const newDamageReports = reports.filter(report => report.new_damage);

        setTotalReports(reports.length);
        setTotalNewDamageReports(newDamageReports.length);
    }

    // TODO: get from parent component
    const retrieveExpenses = () => {
        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();

        axios.get('/ajax/expenses?search_term=' + props.vehicle.vin, { cancelToken: source.token })
        .then((response) => {
            let tickets = 0;
            let damage = 0;
            response?.data.data.data.map((expense)=> {
                if (expense.line_items_count !== 1) {
                    expense.line_items.map((item)=> {
                        if (item.Cat_ID == 6) {
                            tickets++;
                        } else if (item.Cat_ID == 3) {
                            damage++;
                        }
                    })
                } else {
                    if (expense.line_items[0].Cat_ID == 6) {
                        tickets++;
                    } else if (expense.line_items[0].Cat_ID == 3) {
                        damage++;
                    }
                }
            })

            setExpenses(response?.data.data.data);
            setTotalExpenses(response?.data.data.data.reduce((a, b) => a + parseInt(b.Total_Cost), 0));
            setExpenseTickets(tickets);
            setExpenseDamage(damage);
        })

        return source;
    }

    const reloadReports = (value) => {
        const time = value.value

        setStatsTimeline(time);
        setBookingsBreakdown(setupBookingsBreakdown(props.vehicle_details.booking_breakdown, time));
    }

    return(
        <div className="row expanded bkg-white-dark" >
            <div className="columns large-12 medium-12 small-12">
                <GridContainer justifyContent='space-evenly' direction={windowWidth <= 768 ? "column" : "row"}>
                    <GridItem xs={windowWidth <= 768 ? 12 : 8}>
                        <div style={{marginTop: 8}}>
                            <div style={{display: 'flex', flex: 1, flexDirection: windowWidth <= 768 ? 'column' : 'row'}}>
                                <ActivityCard
                                    label="Vehicle Utilization Rate"
                                    iconName="Booking"
                                    onClick={()=> {props.setTab('Bookings')}}
                                    activityCards={[
                                        <ActivityCardItem headerValue={props.vehicle?.utilization ? `${parseInt(props.vehicle?.utilization)}%` : '0%'}/>
                                    ]}
                                />
                                <ActivityCard
                                    label="Previous Bookings"
                                    iconName="Booking"
                                    onClick={()=> {props.setTab('Bookings')}}
                                    activityCards={[
                                        <ActivityCardItem headerValue={props.vehicle_details.bookings?.past ? `${props.vehicle_details.bookings.past.length}` : '0'}/>, 
                                    ]}
                                />
                                <ActivityCard
                                    label="Last Booking"
                                    iconName="Booking"
                                    onClick={()=> {props.setTab('Bookings')}}
                                    activityCards={[
                                        <ActivityCardItem
                                            headerValue={
                                                props.vehicle_details.vehicle.last_booking ?
                                                    moment(props.vehicle_details.vehicle.last_booking.End_Date).format('MMM DD, YYYY')
                                                    : 'None'
                                            }
                                        />
                                    ]}
                                />
                            </div>
                            <div style={{display: 'flex', flex: 1, flexDirection: windowWidth <= 768 ? 'column' : 'row'}}>
                                <ActivityCard
                                    label="Inspections Completed"
                                    iconName="RideReport"
                                    onClick={()=> {props.setTab('RideReports')}}
                                    activityCards={[
                                        <ActivityCardItem
                                            headerValue={`${totalReports}` || '0'}
                                            loading={totalReports === -1}
                                        />
                                    ]}
                                />
                                <ActivityCard
                                    label="Reports with New Damage"
                                    iconName="RideReport"
                                    onClick={()=> {props.setTab('RideReports')}}
                                    activityCards={[
                                        <ActivityCardItem
                                            headerValue={`${totalNewDamageReports}` || '0'}
                                            loading={totalNewDamageReports === -1}
                                        />
                                    ]}
                                />
                                <ActivityCard
                                    label="Expenses"
                                    iconName="Expenses"
                                    onClick={()=> {props.setTab('Expenses')}}
                                    activityCards={[
                                        <ActivityCardItem
                                            loading={`${totalExpenses}` == -1}
                                            headerValue={totalExpenses.toLocaleString('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                            })}
                                        />
                                    ]}
                                />
                            </div>
                            <div style={{display: 'flex', flex: 1, flexDirection: windowWidth <= 768 ? 'column' : 'row', marginLeft: windowWidth <= 768 ? 0 : '17%', marginRight: windowWidth <= 768 ? 0 : '17%'}}>
                                <ActivityCard
                                    label="Reminders"
                                    iconName="Reminder"
                                    onClick={()=> {props.setTab('Reminders')}}
                                    activityCards={[
                                        <ActivityCardItem
                                            headerValue={props.vehicle_details.reminders.active ? `${props.vehicle_details.reminders.active.length}` : "0"} 
                                        />
                                    ]}
                                />
                                <ActivityCard
                                    label="Media"
                                    iconName="Media"
                                    onClick={()=> {
                                        props.vehicle_details.vehicle.has_media_components ? 
                                            props.setTab('Media') : null;
                                    }}
                                    activityCards={[
                                        <ActivityCardItem headerValue={props.vehicle_details.media?.complete ? `${props.vehicle_details.media.complete?.length}` : "0"} labelValue="Completed"/>,
                                        <ActivityCardItem headerValue={props.vehicle_details.media?.outstanding ? `${props.vehicle_details.media.outstanding?.length}` : "0"} labelValue="Outstanding"/>
                                    ]}
                                />
                            </div>
                        </div>
                        <div style={{flex: 1, display: 'flex', flexDirection: windowWidth <= 768 ? 'column' : 'row'}}>
                            <div className="ds-card" style={{ marginTop: 16, display: 'flex', flex: 1, flexDirection: 'column', margin: '16px 8px 8px'}}>
                                <div style={{borderBottom: `solid 1px ${colours.primary.grey03}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'row', paddingBottom: 8, marginBottom: 8}}>
                                    <div>
                                        <P2><Trans>Bookings</Trans></P2>
                                        <P3 style={{marginTop: 4, color: colours.primary.grey02}}>
                                            <Trans>
                                                {moment().subtract(statsTimeline, 'months').format("MMM DD, YYYY")} – {moment().format("MMM DD, YYYY")}
                                            </Trans>
                                        </P3>
                                    </div>
                                    <div style={{display: 'flex', flex: 1, marginLeft: 16, maxWidth: 160}}>
                                        <Select 
                                            defaultValue = {{label:'Last 12 Months',value:12}}
                                            options={
                                                [{label:'Last 12 Months',value:12},{label:'Last 6 Months',value:6},{label:'Last 3 Months',value:3}]
                                            }
                                            onChange={reloadReports}
                                            className='react_select_dropdown'
                                            classNamePrefix='react_select'
                                        />
                                    </div>
                                </div>
                                <div style={{display: 'flex', flex: 1, minHeight: 300, flexDirection: 'column'}}>
                                    <ResponsiveContainer width="99%" height="90%">
                                        <AreaChart data={bookingsBreakdown} dataKey="value" activeDot={{ stroke: 'red', strokeWidth: 2, r: 10 }}
                                                margin={{top: 10, right: 0, left: 0, bottom: 0}}>
                                            <defs>
                                                <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#E1EFFF" stopOpacity={1}/>
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="0"/>
                                            <XAxis dataKey="name"/>
                                            <Tooltip/>
                                            <Area type='monotone' dataKey='Bookings' stroke='#1665D8' fill='#1e7ff0' fill="url(#colorBookings)"/>
                                        </AreaChart>
                                    </ResponsiveContainer >
                                </div>
                            </div>
                            {windowWidth <= 768 ? null : <div style={{marginLeft: 16}}></div>}
                            <div className="ds-card" style={{marginTop: 16, paddingBottom: 16, minHeight: 300, display: 'flex', flex: 1, flexDirection: 'column', margin: '16px 8px 8px'}}>
                                {/* <MediaRating noHeader/> */}
                                <div style={{borderBottom: `solid 1px ${colours.primary.grey03}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'row', paddingBottom: 8, marginBottom: 16,}}>
                                    <P2><Trans>Media Rating</Trans></P2>
                                </div>
                                <div style={{display: 'flex', flex: 1, flexDirection: 'column'}}>
                                    { 
                                        <div style={windowWidth <= 768 ? 
                                            {display: 'flex', flex: 1, height: 250, alignItems: 'center', flexDirection: 'column'}
                                            : {display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'center'}}>
                                            <div style={{display: 'flex', flex: 1, flexDirection: 'row', justifyContent: 'center', marginBottom: 32}}>
                                                <Gauge 
                                                value={props.stakeholder.current_overlay_media_average.average ? 
                                                    props.stakeholder.current_overlay_media_average.average : 0}
                                                // value={75}
                                                />
                                            </div>
                                            <Heading5 style={{textAlign: 'center', marginBottom: 16, alignItems: 'center', color: colours.primary.grey01}}>
                                                <Trans>
                                                    {props.stakeholder.current_overlay_media_average.average >= 0 && props.stakeholder.current_overlay_media_average.average < 20 ? 
                                                    'Negative'
                                                    : props.stakeholder.current_overlay_media_average.average >= 20 && props.stakeholder.current_overlay_media_average.average < 40 ?
                                                    'Somewhat Negative'
                                                    : props.stakeholder.current_overlay_media_average.average >= 40 && props.stakeholder.current_overlay_media_average.average < 60 ?
                                                    'Neutral'
                                                    : props.stakeholder.current_overlay_media_average.average >= 60 && props.stakeholder.current_overlay_media_average.average < 80 ?
                                                    'Somewhat Positive'
                                                    : props.stakeholder.current_overlay_media_average.average >= 80 ?
                                                    'Positive'
                                                    : 'No Ratings Yet'
                                                }
                                                </Trans>
                                            </Heading5>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </GridItem>
                    <GridItem xs={windowWidth <= 768 ? 12 : 4}>
                        <div className="ds-card">
                            <div className="block_activity">
                                <div style={{borderBottom: `solid 1px ${colours.primary.grey03}`, paddingBottom: 8, marginBottom: 8}}>
                                    <P2><Trans>Activity</Trans></P2>
                                </div>
                                <div className="overlay_block_main activity_main">
                                    <div className="overlay_block_activity_timeline">
                                        <VehicleActivity {...props} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </GridItem>
                </GridContainer>
            </div>
        </div>
    )
}

const mapStateToProps = store => ({
    userSession: store.userSession,
    stakeholder: store.stakeholder,
    settings: store.settings,
    data: store.data
})

export default connect(mapStateToProps)(VehicleOverviewTab)
